import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

// Create basic axios instance without Redux dependencies
const api: AxiosInstance = axios.create({
    baseURL: (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api/v1',
    timeout: 60000,
    withCredentials: true, // Important for cookies
    headers: {
        'Content-Type': 'application/json',
    },
})

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (error?: any) => void
}> = []

const processQueue = (error: any = null, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error)
        } else {
            resolve(token)
        }
    })

    failedQueue = []
}

// Request interceptor to add auth token from localStorage
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('token')
        const isRefreshEndpoint = config.url?.includes('/auth/refresh') || config.url?.includes('/auth/refresh-token')

        if (token && !isRefreshEndpoint) {
            config.headers.Authorization = `Bearer ${token}`
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor to handle token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config

        // If error is 401 and we haven't already tried to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true

            const refreshToken = localStorage.getItem('refreshToken')

            // Don't redirect to login for auth endpoints (login, register, etc.)
            const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                originalRequest.url?.includes('/auth/register') ||
                originalRequest.url?.includes('/auth/forgot-password') ||
                originalRequest.url?.includes('/auth/reset-password')

            if (!refreshToken || isAuthEndpoint) {
                // No refresh token or this is an auth endpoint, don't redirect
                if (!isAuthEndpoint) {
                    localStorage.removeItem('token')
                    localStorage.removeItem('refreshToken')
                    window.location.href = '/login'
                }
                return Promise.reject(error)
            }

            if (isRefreshing) {
                // Already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`
                    return api(originalRequest)
                }).catch(err => {
                    return Promise.reject(err)
                })
            }

            isRefreshing = true

            try {
                const response = await axios.post(`${(import.meta.env.VITE_API_URL || 'http://localhost:5000')}/api/v1/auth/refresh`, {
                    refreshToken: refreshToken
                }, {
                    headers: {
                        'Authorization': `Bearer ${refreshToken}`
                    }
                })

                const { token: newToken, refreshToken: newRefreshToken } = response.data.data

                localStorage.setItem('token', newToken)
                localStorage.setItem('refreshToken', newRefreshToken)

                processQueue(null, newToken)

                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return api(originalRequest)
            } catch (refreshError) {
                processQueue(refreshError, null)

                // Refresh failed, clear tokens and redirect to login
                localStorage.removeItem('token')
                localStorage.removeItem('refreshToken')

                // Only redirect if not on auth pages
                if (!window.location.pathname.startsWith('/login') &&
                    !window.location.pathname.startsWith('/register') &&
                    !window.location.pathname.startsWith('/forgot-password') &&
                    !window.location.pathname.startsWith('/reset-password')) {
                    window.location.href = '/login'
                }

                return Promise.reject(refreshError)
            } finally {
                isRefreshing = false
            }
        }

        return Promise.reject(error)
    }
)

export default api